import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Loader2, PartyPopper, RotateCcw, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentReturn() {
  const [params] = useSearchParams();
  const ref = params.get("ref") || "";
  const [state, setState] = useState("checking"); // checking | paid | failed | pending
  const attempts = useRef(0);

  useEffect(() => {
    if (!ref) {
      setState("failed");
      return;
    }
    let cancelled = false;
    const check = async () => {
      try {
        const { data } = await axios.post(`${API}/orders/verify`, { order_id: ref });
        if (cancelled) return;
        if (data.status === "paid") {
          setState("paid");
          return;
        }
        if (data.status === "failed") {
          setState("failed");
          return;
        }
        attempts.current += 1;
        if (attempts.current < 6) {
          setTimeout(check, 3000);
        } else {
          setState("pending");
        }
      } catch (err) {
        if (cancelled) return;
        if (err.response?.status === 404) {
          setState("failed");
          return;
        }
        attempts.current += 1;
        if (attempts.current < 6) {
          setTimeout(check, 3000);
        } else {
          setState("pending");
        }
      }
    };
    check();
    return () => {
      cancelled = true;
    };
  }, [ref]);

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center px-4 py-16" data-testid="payment-return-page">
      <motion.div
        initial={{ opacity: 0, y: 30, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ type: "spring", bounce: 0.3 }}
        className="w-full max-w-md rounded-[2rem] border-[3px] border-ink bg-white p-8 sm:p-10 text-center shadow-[8px_8px_0_#2D1A32]"
      >
        <img src="/logo.png" alt="Vowyo" className="h-9 w-auto mx-auto mb-8" />

        {state === "checking" && (
          <div data-testid="payment-status-checking">
            <span className="inline-flex w-20 h-20 rounded-full bg-sunny border-[3px] border-ink shadow-[5px_5px_0_#2D1A32] items-center justify-center">
              <Loader2 size={34} className="animate-spin" />
            </span>
            <h1 className="mt-6 font-display font-bold text-2xl">Checking your payment…</h1>
            <p className="mt-3 text-sm text-plum font-medium">Hold tight — we're confirming it with the bank. This takes a few seconds.</p>
          </div>
        )}

        {state === "paid" && (
          <div data-testid="payment-status-paid">
            <motion.span initial={{ scale: 0, rotate: -30 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: "spring", bounce: 0.6 }}
              className="inline-flex w-20 h-20 rounded-full bg-mint border-[3px] border-ink shadow-[5px_5px_0_#2D1A32] items-center justify-center">
              <PartyPopper size={34} />
            </motion.span>
            <h1 className="mt-6 font-display font-bold text-2xl">Payment received!</h1>
            <p className="mt-3 text-sm text-plum font-medium">
              Your song is in the studio. We've emailed your receipt and it'll land in your inbox within 12 hours.
            </p>
            <div className="mt-6 inline-block rounded-2xl border-2 border-ink bg-cream px-6 py-3 shadow-[4px_4px_0_#2D1A32]">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-lilac">Order &amp; Tracking ID</p>
              <p className="font-mono text-sm font-bold text-ink mt-0.5" data-testid="payment-order-ref">{ref}</p>
            </div>
            <div>
              <Link to={`/track/${ref}`} data-testid="payment-track-link"
                className="btn-pop mt-7 inline-block rounded-full bg-pink text-white font-head font-bold px-8 py-3 text-sm border-2 border-ink shadow-[4px_4px_0_#2D1A32]">
                Track your order →
              </Link>
            </div>
          </div>
        )}

        {state === "failed" && (
          <div data-testid="payment-status-failed">
            <span className="inline-flex w-20 h-20 rounded-full bg-pink border-[3px] border-ink shadow-[5px_5px_0_#2D1A32] items-center justify-center text-white">
              <RotateCcw size={32} />
            </span>
            <h1 className="mt-6 font-display font-bold text-2xl">Payment didn't go through</h1>
            <p className="mt-3 text-sm text-plum font-medium">
              No money was taken (any held amount is auto-refunded by your bank). Your details are saved — you can try again anytime.
            </p>
            <Link to="/" data-testid="payment-retry-link"
              className="btn-pop mt-7 inline-block rounded-full bg-ink text-white font-head font-bold px-8 py-3 text-sm border-2 border-ink shadow-[4px_4px_0_#FF3B7E]">
              Try again
            </Link>
          </div>
        )}

        {state === "pending" && (
          <div data-testid="payment-status-pending">
            <span className="inline-flex w-20 h-20 rounded-full bg-sunny border-[3px] border-ink shadow-[5px_5px_0_#2D1A32] items-center justify-center">
              <ShieldCheck size={32} />
            </span>
            <h1 className="mt-6 font-display font-bold text-2xl">Still confirming…</h1>
            <p className="mt-3 text-sm text-plum font-medium">
              Your bank is taking a little longer to confirm. As soon as it clears, we'll email your receipt — you can also check your tracking page in a few minutes.
            </p>
            <Link to={`/track/${ref}`} data-testid="payment-pending-track-link"
              className="btn-pop mt-7 inline-block rounded-full bg-pink text-white font-head font-bold px-8 py-3 text-sm border-2 border-ink shadow-[4px_4px_0_#2D1A32]">
              Check order status
            </Link>
          </div>
        )}
      </motion.div>
    </div>
  );
}
